"use client";

import { useMemo, useState } from "react";
import { DocumentPreview } from "@/components/tool-kit/document-preview";
import {
  Panel,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY } from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import {
  buildResearchProposal,
  type ProposalApproach,
  type ResearchProposalInput,
} from "@/lib/tool-kit/essay-templates/research-proposal";

/** English strings. A translated content file overrides these through `ui`. */
export const RESEARCH_PROPOSAL_GENERATOR_STRINGS = {
  tryExample: "Try an example",
  clear: "Clear the form",
  projectHeading: "Your project",
  titleLabel: "Working title",
  titlePlaceholder:
    "e.g. Peer Tutoring and First-Year Retention in Undergraduate STEM",
  wordsLabel: "Target length (words)",
  wordsPlaceholder: "e.g. 3000",
  wordsHint: "Splits the proposal into a word target for each section.",
  problemHeading: "Problem and questions",
  problemLabel: "Research problem",
  problemPlaceholder: "What is happening, why does it matter, and for whom?",
  questionsLabel: "Aims or research questions — one per line",
  questionsPlaceholder:
    "e.g. Does attending peer tutoring predict second-year enrollment?",
  rationaleLabel: "Rationale — the gap in the literature",
  rationalePlaceholder:
    "What have existing studies not done, or done only with limits?",
  methodsHeading: "Methods and plan",
  approachLabel: "Approach",
  approaches: {
    quantitative: "Quantitative",
    qualitative: "Qualitative",
    mixed: "Mixed methods",
    undecided: "Not decided yet",
  },
  methodologyLabel: "Methodology notes (optional)",
  methodologyPlaceholder:
    "Design, participants or data, and analysis — in a few sentences.",
  ethicsLabel: "Ethical considerations (optional)",
  ethicsPlaceholder:
    "Consent, confidentiality, risk, and who approves the study.",
  timelineLabel: "Timeline — one milestone per line (optional)",
  timelinePlaceholder:
    "e.g. Months 1–2: Ethics approval\nMonths 3–5: Data collection",
  outcomesLabel: "Expected outcomes and significance (optional)",
  outcomesPlaceholder:
    "What do you expect to find or produce, and who will benefit?",
  fileName: "research-proposal",
  document: {
    heading: "Your research proposal outline",
    prompt: "To write",
    guidance: "Tip",
    copy: "Copy",
    copied: "Copied!",
    downloadText: "Download .txt",
    downloadMarkdown: "Download .md",
    print: "Print",
    remaining: "one: part still to write|other: parts still to write",
    complete: "Every section has your content.",
  },
  defaultTitle: "Research Proposal",
  subtitle: "Research proposal outline",
  subtitleWords: "Research proposal outline · {words} words",
  budgetHeading: "Word Budget",
  budgetNote:
    "A typical split, not a rule: programs, supervisors, and funders set their own headings and limits, so follow theirs where they differ. The reference list usually sits outside the word count; check whether your title page and abstract count.",
  budget: {
    section: "Section",
    share: "Share",
    words: "Target words",
    total: "Total",
  },
  sections: {
    introduction: "1. Introduction and Background",
    literature: "2. Literature Review and Rationale",
    questions: "3. Aims and Research Questions",
    methodology: "4. Methodology",
    ethics: "5. Ethical Considerations",
    timeline: "6. Timeline",
    outcomes: "7. Expected Outcomes and Significance",
    references: "References",
  },
  target: "Target: about {words} words ({share}).",
  targetShare: "Target: about {share} of the proposal.",
  tips: {
    introduction:
      "Set out the problem and why it matters now, in the present tense.",
    literature:
      "Organize by theme, not by author, and end on the gap your study fills.",
    questions:
      "Each question should be answerable with the method in section 4.",
    methodology:
      "Justify every choice against your questions — reviewers read this section most closely.",
    ethics: "Show that you have anticipated the risks, not just listed rules.",
    timeline:
      "Be realistic: ethics approval and recruitment usually take longer than planned.",
    outcomes:
      "Say what the study will add, without promising results you cannot guarantee.",
    references:
      "Cite every source from the literature review in your required style.",
  },
  subheadings: {
    problem: "Research problem",
    gap: "The gap",
    design: "Research design",
    sample: "Participants or data sources",
    collection: "Data collection",
    analysis: "Data analysis",
    limitations: "Limitations",
  },
  prompts: {
    problem:
      "State the problem in two or three sentences: what is happening, why it matters, and for whom.",
    context:
      "Give the context a reader needs: the setting, the key terms, and what prompted the study.",
    literature:
      "Summarize the main strands of research on the problem, grouped by theme, and cite each claim.",
    gap: "Name the gap precisely: what existing studies have not done, or have done only with limits, that your study addresses.",
    questions:
      "Write one overall aim, then two or three research questions or hypotheses that follow from the gap.",
    limitations:
      "Name the main limitations of the design and how you will reduce their effect.",
    ethics:
      "Explain how you will obtain informed consent, protect confidentiality, store data securely, and minimize risk — and which ethics committee will review the study.",
    timeline:
      "Break the project into phases with dates — ethics approval, data collection, analysis, writing — and leave time for delays.",
    outcomes:
      "Say what you expect to find or produce, and who will benefit: researchers, practitioners, policymakers, or participants.",
    references: "Add your reference list here.",
  },
  design: {
    quantitative:
      "Name the design — experimental, quasi-experimental, survey, or correlational — and explain why it fits your questions.",
    qualitative:
      "Name the design — case study, ethnography, phenomenology, grounded theory, or another — and explain why it fits your questions.",
    mixed:
      "Name the design — convergent, explanatory sequential, or exploratory sequential — and explain how the two strands connect.",
    undecided:
      "Decide whether a quantitative, qualitative, or mixed-methods design fits your questions, and explain why.",
  },
  sample: {
    quantitative:
      "Define the population, the sampling method, and the sample size — ideally justified with a power analysis.",
    qualitative:
      "Describe who you will recruit, how (for example, purposive or snowball sampling), and how you will judge when you have enough data.",
    mixed:
      "Describe the sample for each strand, and whether the same people take part in both.",
    undecided:
      "Describe who or what you will study, how you will select them, and how many.",
  },
  collection: {
    quantitative:
      "Name each instrument or measure, with evidence of its reliability and validity.",
    qualitative:
      "Describe your interviews, focus groups, observations, or documents, and attach draft guides as an appendix.",
    mixed:
      "Describe the instruments for each strand and the order in which they run.",
    undecided:
      "Describe how you will collect your data and which instruments you will use.",
  },
  analysis: {
    quantitative:
      "Match each research question to the statistical test that will answer it.",
    qualitative:
      "Name your analytic approach, such as reflexive thematic analysis, and how you will keep it rigorous.",
    mixed:
      "Explain how you will analyze each strand and where you will integrate the findings.",
    undecided: "Explain how you will analyze the data to answer each question.",
  },
  rqLabel: "RQ{n}:",
  ethicsChecklist: [
    "Informed consent and the right to withdraw",
    "Confidentiality or anonymity",
    "Secure data storage and retention",
    "Risk of harm to participants or researchers",
    "Approval from an ethics committee or IRB",
  ],
  timelineHead: { period: "When", activity: "Activity" },
  example: {
    title:
      "Peer Tutoring and First-Year Retention in Undergraduate STEM Programs",
    words: "3000",
    problem:
      "Nearly a third of first-year STEM students at our university do not return for a second year, and most of them leave after failing a gateway course in mathematics or chemistry. The university funds a peer tutoring program, but nobody knows whether it helps the students most at risk of leaving.",
    questions:
      "Is attending peer tutoring in the first semester associated with re-enrollment in the second year?\nDoes the association differ for first-generation students?\nHow do students who attended tutoring describe its effect on their decision to stay?",
    rationale:
      "Studies of peer tutoring mostly measure course grades rather than whether students stay enrolled, and few report results for first-generation students separately. Accounts from students themselves of why tutoring matters are rarer still.",
    methodology:
      "An explanatory sequential design: first, a logistic regression on three years of anonymized enrollment and tutoring records (about 4,000 students); second, semi-structured interviews with 12–15 students chosen in light of the regression results.",
    ethics:
      "The registrar will anonymize the records before release. Interview participants will give written consent, may withdraw up to two weeks after their interview, and will be identified only by pseudonyms. The university's Institutional Review Board will review the study.",
    timeline:
      "Months 1–2: Ethics approval and data request\nMonths 3–4: Analysis of enrollment records\nMonths 5–6: Interviews\nMonths 7–8: Thematic analysis and integration\nMonth 9: Writing and submission",
    outcomes:
      "The study will show whether tutoring is associated with retention once prior grades are taken into account, and what students say makes the difference. The findings will inform how the university targets its tutoring budget.",
  },
} as const;

const EMPTY: ResearchProposalInput = {
  title: "",
  problem: "",
  questions: "",
  rationale: "",
  approach: "undecided",
  methodology: "",
  ethics: "",
  timeline: "",
  outcomes: "",
  words: "",
};

/** The example's choices that are settings, not prose — kept out of the translatable strings. */
const EXAMPLE_SETTINGS = { approach: "mixed" } as const;

const APPROACHES: readonly ProposalApproach[] = [
  "quantitative",
  "qualitative",
  "mixed",
  "undecided",
];

type TextKey = Exclude<keyof ResearchProposalInput, "approach">;

export default function ResearchProposalGeneratorWidget() {
  const strings = useToolUi(RESEARCH_PROPOSAL_GENERATOR_STRINGS);
  const fmt = useToolNumber();
  const [input, setInput] = useState<ResearchProposalInput>(EMPTY);
  const doc = useMemo(
    () => buildResearchProposal(input, strings, fmt),
    [input, strings, fmt]
  );
  const update = (key: TextKey) => (value: string) =>
    setInput((previous) => ({ ...previous, [key]: value }));
  const approachOptions = APPROACHES.map((value) => ({
    value,
    label: strings.approaches[value],
  }));

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() =>
            setInput({ ...EMPTY, ...strings.example, ...EXAMPLE_SETTINGS })
          }
          type="button"
        >
          {strings.tryExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setInput(EMPTY)}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      <Panel title={strings.projectHeading}>
        <div className="grid gap-4 sm:grid-cols-[1fr_12rem]">
          <TextField
            id="rp-title"
            label={strings.titleLabel}
            onChange={update("title")}
            placeholder={strings.titlePlaceholder}
            value={input.title}
          />
          <TextField
            hint={strings.wordsHint}
            id="rp-words"
            label={strings.wordsLabel}
            numeric
            onChange={update("words")}
            placeholder={strings.wordsPlaceholder}
            value={input.words}
          />
        </div>
      </Panel>

      <Panel title={strings.problemHeading}>
        <TextAreaField
          id="rp-problem"
          label={strings.problemLabel}
          onChange={update("problem")}
          placeholder={strings.problemPlaceholder}
          prose
          rows={3}
          value={input.problem}
        />
        <TextAreaField
          id="rp-questions"
          label={strings.questionsLabel}
          onChange={update("questions")}
          placeholder={strings.questionsPlaceholder}
          prose
          rows={3}
          value={input.questions}
        />
        <TextAreaField
          id="rp-rationale"
          label={strings.rationaleLabel}
          onChange={update("rationale")}
          placeholder={strings.rationalePlaceholder}
          prose
          rows={3}
          value={input.rationale}
        />
      </Panel>

      <Panel title={strings.methodsHeading}>
        <SelectField
          id="rp-approach"
          label={strings.approachLabel}
          onChange={(approach) =>
            setInput((previous) => ({ ...previous, approach }))
          }
          options={approachOptions}
          value={input.approach}
        />
        <TextAreaField
          id="rp-methodology"
          label={strings.methodologyLabel}
          onChange={update("methodology")}
          placeholder={strings.methodologyPlaceholder}
          prose
          rows={3}
          value={input.methodology}
        />
        <TextAreaField
          id="rp-ethics"
          label={strings.ethicsLabel}
          onChange={update("ethics")}
          placeholder={strings.ethicsPlaceholder}
          prose
          rows={3}
          value={input.ethics}
        />
        <TextAreaField
          id="rp-timeline"
          label={strings.timelineLabel}
          onChange={update("timeline")}
          placeholder={strings.timelinePlaceholder}
          prose
          rows={4}
          value={input.timeline}
        />
        <TextAreaField
          id="rp-outcomes"
          label={strings.outcomesLabel}
          onChange={update("outcomes")}
          placeholder={strings.outcomesPlaceholder}
          prose
          rows={3}
          value={input.outcomes}
        />
      </Panel>

      <DocumentPreview
        doc={doc}
        fileName={strings.fileName}
        idPrefix="rp"
        labels={strings.document}
      />
    </div>
  );
}
